import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Folder, Requirement, Status } from "../interfaces/folder.types.ts";
import {
  fetchFolderById,
  fetchVaultRequirementById,
} from "../actions/vaultActions.tsx";
import { onLogout } from "../actions/authActions.tsx";
import { getStoredVaultActorToken } from "../../utils/vault/vaultActorSession.ts";

interface VaultState {
  activeRequirement: Requirement | null;
  activeVault: Folder | null;
  jwtToken: string | null;
  loading: boolean;
  activePanels: string[];
  activeTab: string | null;
}

const initialState = {
  activeRequirement: null,
  activeVault: null,
  jwtToken: getStoredVaultActorToken(),
  loading: false,
  activePanels: [],
  activeTab: null,
} satisfies VaultState as VaultState;

const vaultSlice = createSlice({
  name: "vault",
  initialState,
  reducers: {
    setActiveRequirement(state, action: PayloadAction<Requirement>) {
      state.activeRequirement = action.payload;
    },
    clearActiveRequirement(state) {
      state.activeRequirement = null;
    },
    setActiveVault(state, action: PayloadAction<Folder>) {
      state.activeVault = action.payload;
    },
    clearActiveVault(state) {
      state.activeVault = null;
    },
    resetVaultViewState(state) {
      state.activeRequirement = null;
      state.activeVault = null;
      state.activePanels = [];
      state.activeTab = null;
    },
    setJWT(state, action: PayloadAction<string>) {
      state.jwtToken = action.payload;
    },
    clearJWT(state) {
      state.jwtToken = null;
    },
    setActivePanels(state, action: PayloadAction<string[]>) {
      state.activePanels = action.payload;
    },
    setActiveVaultTab(state, action: PayloadAction<string>) {
      state.activeTab = action.payload;
    },
    // Marks a single requirement complete by its UNIQUE id. Slug is intentionally NOT
    // used: several subfolders of the same role (one per participant) can share
    // requirement slugs, which would mark the wrong participant's requirement.
    markRequirementAsCompleteById(state, action: PayloadAction<string>) {
      if (state.activeVault?.subfolders) {
        for (const subfolder of state.activeVault.subfolders) {
          if (subfolder.requirements) {
            for (const requirement of subfolder.requirements) {
              if (requirement.id === action.payload) {
                requirement.status = Status.Valid;
                return;
              }
            }
          }
        }
      }
    },
  },
  extraReducers: (builder) => {
    // fetchVaultRequirementById — still used by out-of-scope components (FileComponent.tsx)
    builder.addCase(fetchVaultRequirementById.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(fetchVaultRequirementById.fulfilled, (state, action) => {
      state.activeRequirement = action.payload;
      state.loading = false;
    });

    builder.addCase(fetchVaultRequirementById.rejected, (state) => {
      state.loading = false;
    });

    // fetchFolderById — still used by out-of-scope components (DigitalSignature.tsx, AddTemplatesToFolder.tsx)
    builder.addCase(fetchFolderById.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(fetchFolderById.fulfilled, (state, action) => {
      state.activeVault = action.payload;
      state.loading = false;

      // Sync activeRequirement with the freshly fetched data
      // This ensures polling/refetch updates are reflected in components that read activeRequirement
      if (state.activeRequirement && action.payload?.subfolders) {
        for (const subfolder of action.payload.subfolders) {
          const updated = subfolder.requirements?.find(
            (r: any) => r.id === state.activeRequirement!.id,
          );
          if (updated) {
            state.activeRequirement = updated;
            break;
          }
        }
      }
    });

    builder.addCase(fetchFolderById.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(onLogout.fulfilled, (state) => {
      state.jwtToken = null;
      state.activeRequirement = null;
      state.activeVault = null;
      state.activePanels = [];
      state.activeTab = null;
    });
  },
});

export const {
  setActiveRequirement,
  clearActiveRequirement,
  setActiveVault,
  setJWT,
  clearJWT,
  clearActiveVault,
  resetVaultViewState,
  setActivePanels,
  setActiveVaultTab,
  markRequirementAsCompleteById,
} = vaultSlice.actions;
export default vaultSlice.reducer;
